
import { useState } from "react";
import { FormType, ContactFormData, Profile } from "../types/contact-widget";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export const useContactWidget = (profile: Profile) => {
  const [open, setOpen] = useState(false);
  const [showText, setShowText] = useState(true);
  const [formType, setFormType] = useState<FormType>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState<ContactFormData>({
    name: "",
    email: "",
    phone: "",
    scheduledDate: "",
    message: "",
  });

  const handleEmailClick = () => {
    if (profile.email) {
      window.location.href = `mailto:${profile.email}`;
      setOpen(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = (data: ContactFormData, type: FormType): string | null => {
    if (!data.name.trim()) return "Name is required";
    if (!data.email.trim()) return "Email is required";
    if (!data.message.trim()) return "Message is required";
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(data.email.trim())) return "Invalid email format";
    
    if (type === 'booking' && !data.scheduledDate) {
      return "Please select a date and time";
    }

    if (data.phone) {
      const phoneRegex = /^(\+63|0)9\d{9}$/;
      if (!phoneRegex.test(data.phone)) {
        return "Invalid phone number format (e.g., 09123456789)";
      }
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    // Check if the form type is enabled
    if ((formType === 'lead' && !profile.show_lead_form) || 
        (formType === 'booking' && !profile.show_booking_form)) {
      toast.error("This form is currently disabled", {
        duration: 3000
      });
      return;
    }

    // Validate form data
    const validationError = validateForm(formData, formType);
    if (validationError) {
      toast.error(validationError, {
        duration: 3000
      });
      return;
    }

    setSubmitting(true);
    try {
      const submissionData = {
        profile_id: profile.id,
        form_type: formType,
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim() || null,
        scheduled_date: formType === 'booking' ? formData.scheduledDate : null,
        message: formData.message.trim()
      };

      const { error } = await supabase
        .from("form_submissions")
        .insert([submissionData]);

      if (error) throw error;

      toast.success(
        formType === 'booking' 
          ? "Meeting request submitted successfully!" 
          : "Message sent successfully!",
        {
          duration: 3000
        }
      );
      
      setOpen(false);
      setFormType(null);
      setFormData({
        name: "",
        email: "",
        phone: "",
        scheduledDate: "",
        message: "",
      });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error(
        error.message === "JWT expired" 
          ? "Session expired. Please refresh the page." 
          : "Failed to submit form. Please try again.",
        {
          duration: 3000
        }
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Filter available form types based on profile settings
  const getAvailableFormTypes = () => {
    const types = [];
    if (profile.show_lead_form) types.push('lead');
    if (profile.show_booking_form) types.push('booking');
    return types;
  };

  return {
    open,
    setOpen,
    showText,
    formType,
    setFormType,
    submitting,
    formData,
    setFormData,
    handleEmailClick,
    handleSubmit,
    handleChange,
    getAvailableFormTypes
  };
};
